import React, { Ref, forwardRef, useRef } from "react";

import { IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { Picture } from "../../../models/picture";
import { convertEventToFile } from "../../../util/convertEventToFile";
import { base, BASE64_REGEX } from "../../../util/convertToBase64";
import { FileType } from "../../../util/enums";
import ImageCarouselModal, { ImageCarouselModalHandle, File } from "../../ImageCarouselModal";
import useStyles from "./styles";

type PicturesComponentProps = {
  pictures: Array<string | null>;
  roomPictures?: Picture[];
  page?: string;
  description: string;
  classes?: string;
  viewOnly?: boolean;
  isExpanded?: boolean;
  addPhoto: (pictures: string[]) => void;
  deletePhoto: (pictureIndex: number, pictureString?: string) => void;
};

const PicturesComponent = forwardRef(
  (
    {
      classes,
      pictures,
      roomPictures,
      page,
      description,
      isExpanded,
      addPhoto,
      deletePhoto,
      viewOnly,
    }: PicturesComponentProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const classStyle = useStyles();

    const { t: translate } = useTranslation();
    const imageCarouselModalRef = useRef<ImageCarouselModalHandle>(null);

    const handleFileRead = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = await convertEventToFile(event, translate, ["jpg", "png", "jpeg"]);

      if (file && file?.uri) {
        const base64Splitted = file?.uri?.split(";base64,");

        if (base64Splitted && base64Splitted?.length > 0 && base64Splitted[1]) {
          addPhoto([base64Splitted[1]]);
        }
      }
      event.target.value = "";
    };

    const renderRemoveButton = (index: number, mediaLink: string) => {
      if (!viewOnly) {
        return (
          <IconButton
            className={classStyle.buttonDeletePhoto}
            onClick={() => {
              deletePhoto(index, mediaLink);
            }}
          >
            <Close htmlColor="#FFFFFF" className={classStyle.icon} />
          </IconButton>
        );
      }

      return null;
    };

    const onOpenImgModal = (source: string) => {
      const pictureFiles = pictures.map(
        (picture) =>
          ({
            url: BASE64_REGEX.test(picture ?? "") ? `${base()}${picture}` : picture,
            type: FileType.IMAGE,
          } as File)
      );

      let imageFiles: File[] = [];

      if (roomPictures) {
        imageFiles = roomPictures.map(
          (roomPicture) => ({ url: roomPicture.mediaLink, type: FileType.IMAGE } as File)
        );
      }

      const images = [...pictureFiles, ...imageFiles];
      const indexOfImage = images.map((image) => image.url).indexOf(source);

      imageCarouselModalRef?.current?.handleOpenModal(indexOfImage, images);
    };

    const renderImage = (index: number, mediaLink?: string, source?: string) => {
      if (index !== undefined && mediaLink && source) {
        return (
          <div className={classStyle.photoCard} key={index}>
            {renderRemoveButton(index, mediaLink)}
            <img
              src={source}
              alt="photo"
              className={classStyle.image}
              width={150}
              onClick={() => onOpenImgModal(source)}
            />
          </div>
        );
      }

      return null;
    };

    return (
      <div className={classes}>
        <ImageCarouselModal ref={imageCarouselModalRef} />
        <input
          type="file"
          accept=".jpeg,.png,.jpg"
          className="d-none"
          ref={ref}
          onChange={handleFileRead}
        />
        {page && !isExpanded ? null : (
          <>
            {page &&
            ((pictures && pictures.length > 0) || (roomPictures && roomPictures?.length > 0)) ? (
              <span className={classStyle.componentTitle}>{`${translate(
                "photosOf"
              )} ${description}`}</span>
            ) : null}
            <div className="d-flex align-items-center">
              {pictures?.map((photo, index) => {
                if (!photo) return null;
                const source: string = BASE64_REGEX.test(photo) ? `${base()}${photo}` : photo;

                return renderImage(index, photo, source);
              })}
              {roomPictures?.map(({ mediaLink }, index) => {
                return renderImage(index, mediaLink, mediaLink);
              })}
            </div>
          </>
        )}
      </div>
    );
  }
);

export default PicturesComponent;
